import React, {useMemo, useState} from 'react';
import {Table, Box, Header, Toggle} from '@amzn/awsui-components-react';
import {useCollection} from '@amzn/awsui-collection-hooks';
import {POST_PROCESS_PARENT_METRIC, resolveDatesWithTableMetrics} from './model-explainer-data';

export default function ModelExplainerTable({
    data,
    finalColumns,
    postProcessColumns,
    modelComponentColumns,
    friendlyNameMapping,
    // Model-specific rendering knobs; defaults preserve the original BOSCO behavior.
    forecastColumns = ['cd_mean', 'raw_forecast', 'final_forecast'],
    percentageBaseColumn = 'cd_mean',
    percentageToggleLabel = 'Show % of Training CD Average',
}) {
    const [showPercentages, setShowPercentages] = useState<boolean>(false);

    // The agg response covers every balance date that has any insight, which
    // for models publishing pre-forecast insights (e.g. recent actuals) also
    // includes dates carrying no forecast rows at all. Those would render as
    // empty columns, so keep only dates that have at least one table metric.
    // When every date carries forecast data this is a no-op.
    const uniqueDates: any[] = useMemo(
        () => resolveDatesWithTableMetrics(data, finalColumns, postProcessColumns),
        [data, finalColumns, postProcessColumns]
    );

    const tableData: any[] = useMemo(
        () => transformData(data, uniqueDates, finalColumns, modelComponentColumns, postProcessColumns, percentageBaseColumn),
        [data, uniqueDates, finalColumns, modelComponentColumns, postProcessColumns, percentageBaseColumn]
    );

    const {items: rootItems, collectionProps} = useCollection(tableData, {
        expandableRows: {
            getId: (item) => item.metric,
            getParentId: (item) => item.parentMetric,
        },
    });

    function formatDateWithDayOfWeek(dateString: string): string {
        // Parse the date string manually to avoid timezone issues
        const [year, month, day] = dateString.split('-').map(Number);
        const date = new Date(year, month - 1, day); // month is 0-indexed
        const dayOfWeek = date.toLocaleDateString('en-US', {weekday: 'short'});
        return `${dateString} (${dayOfWeek})`;
    }

    function getMetricValue(item: any, date: string) {
        const value = item[date].value;
        const isNumber = typeof value === 'number';
        const formattedValue = isNumber ? value.toFixed(2) : value;

        // Forecast-level metrics are rendered bold without +/- color coding
        const isForecastMetric = forecastColumns.includes(item.metric);

        // Calculate percentage relative to the model's percentage base column
        let percentageText = '';
        if (item[date].percentage) {
            // Find the raw_forecast value for this date
            const percentage = item[date].percentage * 100;
            percentageText = ` (${percentage.toFixed(2)}%)`;
        }

        if (isForecastMetric) {
            return <b>{formattedValue}</b>;
        }

        // For other metrics, apply color coding
        if (isNumber) {
            const visuallyFormattedValue = value >= 0 ? `+${formattedValue}` : formattedValue;

            return (
                <span style={{color: value >= 0 ? 'green' : 'red'}}>
                    {visuallyFormattedValue}
                    {showPercentages ? percentageText : ''}
                </span>
            );
        }

        return formattedValue;
    }

    return (
        <Table
            data-testid="page-table"
            stickyHeader
            stripedRows
            stickyColumns={{first: 1}}
            columnDefinitions={[
                {
                    id: 'metric',
                    header: 'Metric',
                    isRowHeader: true,
                    cell: (item: any) => <b>{friendlyNameMapping[item.metric]}</b>,
                },
                ...uniqueDates.map((date) => ({
                    id: date,
                    header: formatDateWithDayOfWeek(date),
                    isRowHeader: true,
                    cell: (item: any) => getMetricValue(item, date),
                })),
            ]}
            header={
                <Header
                    actions={
                        <Toggle
                            data-testid="percentage-toggle"
                            onChange={({detail}) => setShowPercentages(detail.checked)}
                            checked={showPercentages}
                        >
                            {percentageToggleLabel}
                        </Toggle>
                    }
                />
            }
            items={rootItems}
            {...collectionProps}
            empty={
                <Box textAlign="center" color="inherit">
                    <b>No matching data</b>
                    <Box variant="p" color="inherit">
                        There is no matching data to display
                    </Box>
                </Box>
            }
        ></Table>
    );
}

function transformData(
    data: any[],
    uniqueDates: string[],
    finalColumns: string[],
    modelComponentColumns: string[],
    postProcessColumns: string[],
    percentageBaseColumn: string
) {
    // With no dates there is nothing to tabulate. Returning rows here would
    // render every metric name against zero date columns, which looks like a
    // broken table rather than an empty one.
    if (!data.length || !finalColumns.length || !uniqueDates.length) return [];

    const aggData = data.map((record) => {
        return {
            ...record,
            post_process_contributions: postProcessColumns.reduce(
                (accumulator, currentValue) => accumulator + (record[currentValue] || 0),
                0
            ),
        };
    });

    // Create pivoted structure. A configured column that no row in the data
    // carries is dropped rather than rendered as a labelled row of blanks: the
    // set of insight types a model run produces changes over time, and older
    // and newer runs are viewed through the same config. The synthetic
    // post-process parent row is always kept.
    const producedColumns = new Set(aggData.flatMap((record) => Object.keys(record).filter((key) => record[key] != null)));
    return [...finalColumns, ...postProcessColumns]
        .filter((column) => column === POST_PROCESS_PARENT_METRIC || producedColumns.has(column))
        .map((column) => {
            const row: any = {metric: column};
            uniqueDates.forEach((date: any) => {
                const dataForDate = aggData.find((item) => item.balanceDate === date);
                row[date] = {
                    value: dataForDate ? dataForDate[column] : 0,
                    percentage: modelComponentColumns.includes(column)
                        ? dataForDate
                            ? dataForDate[column] / dataForDate[percentageBaseColumn]
                            : 0
                        : null,
                };
            });
            row.parentMetric = postProcessColumns.includes(column) ? POST_PROCESS_PARENT_METRIC : null;
            return row;
        });
}
